import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import { useQueryKey, useRenameQueryResult } from "@/services/utils"
import type { HttpError } from "@/utils/api"
import { httpClient, toHttpError } from "@/utils/api"
import { useQuery } from "@tanstack/react-query"

interface ResourceType {
  id: string
}

const sortResourceTypes = (resourceTypes: string[]): string[] => {
  return [...resourceTypes].sort((left, right) =>
    left.localeCompare(right, undefined, {
      numeric: true,
      sensitivity: "base",
    })
  )
}

const getResourceTypes = async (
  organizationId: Organization["id"]
): Promise<string[]> => {
  try {
    const response = await httpClient.get<ResourceType[]>(
      `/api/v1/organizations/${organizationId}/pipelines/resource-types`
    )

    return sortResourceTypes(
      response.data.map((resourceType) => resourceType.id)
    )
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

interface UseResourceTypesOptions {
  organizationId: Organization["id"]
}

export const useResourceTypes = ({
  organizationId,
}: UseResourceTypesOptions) => {
  const queryKey = useQueryKey(ApiQueryName.ResourceTypes, "getMany", null, {
    organizationId,
  })

  const queryResult = useQuery<string[], HttpError>({
    enabled: Boolean(organizationId),
    placeholderData: [],
    queryFn: async () => getResourceTypes(organizationId),
    queryKey,
  })

  return useRenameQueryResult(queryResult, ApiQueryName.ResourceTypes)
}
